/**
 * Registration Wizard Page
 *
 * Multi-step registration flow for Individual, Broker, and Company users.
 * Orchestrates step navigation and renders appropriate components.
 *
 * Flow (Phase 9):
 *   Individual:    account -> email-verify -> review -> success
 *   Broker/Company: account -> email-verify -> business-info -> subscription -> [payment] -> review
 *
 * Email verification happens right after account entry (before business details).
 * signUp() is called when the user advances FROM account TO email-verify.
 * verifyRegistration() is called within the email-verify step.
 * After OTP success, the user is authenticated and the wizard continues.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Loader2, Save } from 'lucide-react';

import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Stepper from '../../components/base/Stepper';
import Button from '../../components/base/Button';

import { useRegistrationWizard } from '../../hooks/useRegistrationWizard';
import {
  TierSelector,
  PromoCodeInput,
  BusinessInfoForm,
  AccountInfoForm,
  PaymentForm,
  OtpInput,
} from '../../components/registration';
import { useAuth } from '../../hooks/useAuth';
import type { StoredSession } from '../../hooks/useAuth';
import registrationService from '../../services/registrationService';
import type { UserType } from '../../services/registrationService';
import { apiRequest } from '../../utils/apiClient';
import IntentSignupFlow from '../../components/registration/intent/IntentSignupFlow';
import type { ActivityType } from '../../components/registration/intent/roleCatalog';

// Roles a "Register with us" nav card may deep-link in via ?role= (Bucket A).
const REGISTERABLE_ROLES: ActivityType[] = ['DEVELOPER', 'BROKER', 'PROPERTY_MANAGEMENT', 'FACILITIES_MANAGEMENT', 'APPRAISER', 'CONSULTANT'];

// F1 — individual-first signup. When ON, a fresh /register (no ?type=, no draft)
// drops straight into the individual flow instead of the type-chooser, with a
// "broker/company" fast-path link back to the chooser. Flag OFF => unchanged.
const SIGNUP_INDIVIDUAL_FIRST = import.meta.env.VITE_SIGNUP_INDIVIDUAL_FIRST === 'true';

// User type selection component (inline for simplicity)
function UserTypeSelector({
  selectedType,
  onSelect,
}: {
  selectedType: UserType | null;
  onSelect: (type: UserType) => void;
}) {
  const { t, i18n } = useTranslation('registration');
  const isRTL = i18n.language === 'ar';

  // The developer account type is flag-gated until launch.
  const developerSignupEnabled = import.meta.env.VITE_DEVELOPER_SIGNUP_ENABLED === 'true';
  const types: { value: UserType; icon: string }[] = [
    { value: 'individual', icon: 'ri-user-line' },
    { value: 'broker', icon: 'ri-briefcase-line' },
    { value: 'company', icon: 'ri-building-line' },
    ...(developerSignupEnabled ? [{ value: 'developer' as UserType, icon: 'ri-building-4-line' }] : []),
  ];

  return (
    <div className="max-w-lg mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
          {t('userType.title')}
        </h2>
        <p className="text-gray-500">{t('userType.subtitle')}</p>
      </div>

      <div className="space-y-3">
        {types.map((type) => (
          <button
            key={type.value}
            type="button"
            onClick={() => onSelect(type.value)}
            className={`
              w-full flex items-center p-4 rounded-xl border-2 transition-all text-start
              ${selectedType === type.value
                ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/10'
                : 'border-gray-200 dark:border-gray-700 hover:border-[#1B7566]'
              }
            `}
          >
            <div className={`
              flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center
              ${selectedType === type.value ? 'bg-[var(--pv-color-surface-sunken)]' : 'bg-gray-100 dark:bg-gray-800'}
            `}>
              <i className={`${type.icon} text-2xl ${
                selectedType === type.value ? 'text-[#004D40]' : 'text-gray-500'
              }`}></i>
            </div>
            <div className={`${isRTL ? 'mr-4' : 'ml-4'} flex-1`}>
              <p className={`font-medium ${
                selectedType === type.value ? 'text-[#003930]' : 'text-gray-900 dark:text-white'
              }`}>
                {t(`userType.${type.value}.title`)}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">
                {t(`userType.${type.value}.description`)}
              </p>
            </div>
            <div className={`
              w-5 h-5 rounded-full border-2 flex items-center justify-center
              ${selectedType === type.value
                ? 'border-[#004D40] bg-[#004D40]'
                : 'border-gray-300'
              }
            `}>
              {selectedType === type.value && (
                <i className="ri-check-line text-white text-sm"></i>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Email verification step — renders inline as a wizard step
function EmailVerifyStep({
  email,
  alreadyVerified,
  onVerified,
  onChangeEmail,
  onContinue,
}: {
  email: string;
  // True when the user already verified this email earlier in the same
  // wizard session. We render a "verified" confirmation instead of the OTP
  // input — otherwise the user would land here from an Edit-back navigation
  // with no valid OTP (the in-memory store deletes codes after first use).
  alreadyVerified: boolean;
  onVerified: (pendingSession: StoredSession) => void;
  onChangeEmail: () => void;
  onContinue: () => void;
}) {
  const { t, i18n } = useTranslation('registration');
  const isRTL = i18n.language === 'ar';
  const { verifyRegistration, resendRegistrationOtp } = useAuth();

  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const lastSubmittedCode = useRef('');

  // Countdown for initial resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerify = useCallback(async () => {
    if (otpCode.length !== 6 || isVerifying || otpCode === lastSubmittedCode.current) return;

    setIsVerifying(true);
    setError('');
    lastSubmittedCode.current = otpCode;
    try {
      const pendingSession = await verifyRegistration(email, otpCode);
      // Don't reset isVerifying on success — component will unmount as we advance
      onVerified(pendingSession);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('emailVerify.invalidCode');
      setError(message);
      setIsVerifying(false);
    }
  }, [otpCode, email, isVerifying, verifyRegistration, onVerified, t]);

  // Auto-submit when all 6 digits are entered (only once per unique code)
  useEffect(() => {
    if (otpCode.length === 6 && !isVerifying && otpCode !== lastSubmittedCode.current) {
      handleVerify();
    }
  }, [otpCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0) return;

    setError('');
    try {
      await resendRegistrationOtp(email);
      setResendCooldown(60);
      setOtpCode('');
      lastSubmittedCode.current = '';
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resend code. Please try again.';
      setError(message);
    }
  }, [email, resendCooldown, resendRegistrationOtp]);

  if (alreadyVerified) {
    return (
      <div className="max-w-md mx-auto text-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
          <i className="ri-checkbox-circle-fill text-4xl text-green-500" aria-hidden="true" />
        </div>
        <h2 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
          {isRTL ? 'تم التحقق من البريد الإلكتروني' : 'Email already verified'}
        </h2>
        <p className="text-gray-500 mb-6 break-all">{email}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={onContinue}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#004D40] text-white font-medium hover:shadow-lg transition-shadow"
          >
            {isRTL ? 'متابعة' : 'Continue'}
            <i className={isRTL ? 'ri-arrow-left-line' : 'ri-arrow-right-line'} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onChangeEmail}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <i className="ri-mail-line" aria-hidden="true" />
            {isRTL ? 'تغيير البريد الإلكتروني' : 'Change email'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="text-center mb-8">
        <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 flex items-center justify-center">
          <i className="ri-mail-check-line text-3xl text-[#004D40]" aria-hidden="true"></i>
        </div>
        <h2 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
          {t('emailVerify.title')}
        </h2>
        <p className="text-gray-500 text-sm">
          {t('emailVerify.subtitle')}{' '}
          <span className="font-medium text-gray-700 dark:text-gray-300" dir="ltr">
            {email}
          </span>
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-xl" role="alert">
          <i className="ri-error-warning-line mr-2" aria-hidden="true"></i>
          {error}
        </div>
      )}

      <div className="mb-8">
        <p className="text-sm text-center text-gray-500 mb-4">
          {t('emailVerify.enterCode')}
        </p>
        <OtpInput
          value={otpCode}
          onChange={(val) => {
            setOtpCode(val);
            if (error) setError('');
          }}
          disabled={isVerifying}
          autoFocus
        />
      </div>

      <Button
        onClick={handleVerify}
        className="w-full mb-4"
        disabled={otpCode.length !== 6 || isVerifying}
        loading={isVerifying}
      >
        {isVerifying ? t('buttons.loading') : t('emailVerify.verify')}
      </Button>

      <div className="text-center space-y-3">
        <button
          type="button"
          onClick={handleResend}
          disabled={resendCooldown > 0}
          className={`text-sm transition-colors ${
            resendCooldown > 0
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-[#004D40] hover:text-[#003930] cursor-pointer'
          }`}
        >
          {resendCooldown > 0
            ? t('emailVerify.resendIn', { seconds: resendCooldown })
            : t('emailVerify.resend')}
        </button>

        <div>
          <button
            type="button"
            onClick={onChangeEmail}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <i className={`ri-arrow-left-line ${isRTL ? 'ml-1' : 'mr-1'}`} aria-hidden="true"></i>
            {t('emailVerify.differentEmail')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Review step component for broker/company
function ReviewStep({
  state,
  tiers,
  steps,
  onEdit,
}: {
  state: {
    userType: UserType | null;
    businessInfo: {
      companyName?: string;
      falLicense?: string;
      phone?: string;
    };
    selectedTierId: string | null;
    billingCycle: 'monthly' | 'yearly';
    skippedMembership: boolean;
  };
  tiers: Array<{
    id: string;
    name: string;
    nameAr?: string;
    priceMonthly: number;
    priceYearly: number;
  }>;
  // The wizard's full step sequence - used to derive the correct index
  // for Edit buttons (broker/company have different ordering than the
  // individual flow, so hardcoded 0/1 was sending users to the wrong step).
  steps: ReadonlyArray<{ id: string; label: string }>;
  onEdit: (step: number) => void;
}) {
  const businessInfoIndex = steps.findIndex((s) => s.id === 'business-info');
  const subscriptionIndex = steps.findIndex((s) => s.id === 'subscription');
  const { t, i18n } = useTranslation('registration');
  const isRTL = i18n.language === 'ar';

  const selectedTier = tiers.find((tier) => tier.id === state.selectedTierId);
  const price = state.billingCycle === 'yearly'
    ? selectedTier?.priceYearly
    : selectedTier?.priceMonthly;

  return (
    <div className="max-w-2xl mx-auto space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
          {t('review.title')}
        </h2>
        <p className="text-gray-500">{t('review.subtitle')}</p>
      </div>

      {/* Business Details */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-medium text-gray-900 dark:text-white">
            {t('review.businessDetails')}
          </h3>
          <button
            type="button"
            onClick={() => businessInfoIndex !== -1 && onEdit(businessInfoIndex)}
            className="text-sm text-[#004D40] hover:text-[#003930]"
          >
            {t('review.edit')}
          </button>
        </div>
        <dl className="space-y-2 text-sm">
          {(state.userType === 'company' || state.userType === 'developer') && state.businessInfo.companyName && (
            <div className="flex justify-between">
              <dt className="text-gray-500">{t('businessInfo.companyName')}</dt>
              <dd className="font-medium">{state.businessInfo.companyName}</dd>
            </div>
          )}
          {state.businessInfo.falLicense && (
            <div className="flex justify-between">
              <dt className="text-gray-500">{t('businessInfo.falLicense')}</dt>
              <dd className="font-medium font-mono">{state.businessInfo.falLicense}</dd>
            </div>
          )}
          {state.businessInfo.phone && (
            <div className="flex justify-between">
              <dt className="text-gray-500">{t('businessInfo.phone')}</dt>
              <dd className="font-medium" dir="ltr">{state.businessInfo.phone}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Subscription Details — only if tier selected */}
      {selectedTier && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium text-gray-900 dark:text-white">
              {t('review.subscriptionDetails')}
            </h3>
            <button
              type="button"
              onClick={() => subscriptionIndex !== -1 && onEdit(subscriptionIndex)}
              className="text-sm text-[#004D40] hover:text-[#003930]"
            >
              {t('review.edit')}
            </button>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">{t('payment.plan')}</dt>
              <dd className="font-medium">
                {isRTL ? selectedTier.nameAr || selectedTier.name : selectedTier.name}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">{t('payment.billing')}</dt>
              <dd className="font-medium">
                {state.billingCycle === 'yearly'
                  ? t('subscription.yearly')
                  : t('subscription.monthly')}
              </dd>
            </div>
            <div className="flex justify-between border-t pt-2 mt-2">
              <dt className="font-medium">{t('payment.total')}</dt>
              <dd className="font-bold text-[#004D40]">
                {price != null ? new Intl.NumberFormat(i18n.language).format(price) : ''} <span className="text-sm">SAR</span>
              </dd>
            </div>
          </dl>
        </div>
      )}

      {/* Skipped membership notice */}
      {state.skippedMembership && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <p className="text-sm text-amber-700 dark:text-amber-400">
            <i className="ri-information-line mr-2"></i>
            {t('subscription.skipNote')}
          </p>
        </div>
      )}

      {/* Terms */}
      <p className="text-sm text-gray-500 text-center">
        {t('review.termsText')}{' '}
        <Link to="/terms" className="text-[#004D40] hover:underline">
          {t('review.termsLink')}
        </Link>{' '}
        {t('review.andText')}{' '}
        <Link to="/privacy" className="text-[#004D40] hover:underline">
          {t('review.privacyLink')}
        </Link>
      </p>
    </div>
  );
}

export default function RegisterPage() {
  // Intent-first "easy signup" (services-expansion) replaces the legacy
  // account-type chooser when the flag is on; the legacy wizard below remains the
  // fallback. useSearchParams is called unconditionally so the rules-of-hooks
  // stay satisfied (LegacyRegisterPage's many hooks live in that component).
  const [searchParams] = useSearchParams();
  if (SIGNUP_INDIVIDUAL_FIRST) {
    const roleParam = searchParams.get('role');
    const initialRole = roleParam && REGISTERABLE_ROLES.includes(roleParam as ActivityType)
      ? (roleParam as ActivityType)
      : undefined;
    return <IntentSignupFlow initialRole={initialRole} />;
  }
  return <LegacyRegisterPage />;
}

function LegacyRegisterPage() {
  const { t, i18n } = useTranslation('registration');
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, activateSession } = useAuth();

  const wizard = useRegistrationWizard();
  const {
    state,
    isLoading,
    isSaving,
    draftId,
    currentStep,
    steps,
    canGoPrevious,
    goNext,
    goPrevious,
    goToStep,
    setUserType,
    updateAccountInfo,
    updateBusinessInfo,
    setSelectedTier,
    setBillingCycle,
    setPromoCode,
    skipMembership,
    setEmailVerified,
    clearDraft,
  } = wizard;

  // Draft recovery state
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [draftChecked, setDraftChecked] = useState(false);

  const [tiers, setTiers] = useState<Array<{
    id: string;
    name: string;
    nameAr?: string;
    priceMonthly: number;
    priceYearly: number;
  }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [_stepValid, setStepValid] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [pendingSession, setPendingSession] = useState<StoredSession | null>(null);
  // F1: ensures the individual-first default fires at most once, so the
  // "broker/company" fast-path (which resets userType to null) isn't bounced
  // straight back into the individual flow.
  const didDefaultIndividual = useRef(false);

  // Check if draft exists and show prompt
  useEffect(() => {
    if (!isLoading && !draftChecked) {
      setDraftChecked(true);
      if (draftId && state.userType) {
        setShowDraftPrompt(true);
      }
    }
  }, [isLoading, draftChecked, draftId, state.userType]);

  // Handle starting fresh
  const handleStartFresh = useCallback(async () => {
    setShowDraftPrompt(false);
    await clearDraft();
  }, [clearDraft]);

  // Handle continuing from draft
  const handleContinueDraft = useCallback(() => {
    setShowDraftPrompt(false);
  }, []);

  // Check URL param for initial user type
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const allowedTypes = ['individual', 'broker', 'company'];
    if (import.meta.env.VITE_DEVELOPER_SIGNUP_ENABLED === 'true') allowedTypes.push('developer');
    if (typeParam && allowedTypes.includes(typeParam) && !state.userType) {
      setUserType(typeParam as UserType);
    }
  }, [searchParams, state.userType, setUserType]);

  // F1 — individual-first default. On a fresh visit (no explicit ?type=, no
  // draft to restore) drop straight into the individual flow. Runs at most once
  // (didDefaultIndividual) so the back / "register as a business" link can still
  // return to the chooser without being bounced back. Driving setUserType is the
  // exact action the chooser performs, so the step machinery is untouched.
  useEffect(() => {
    if (!SIGNUP_INDIVIDUAL_FIRST || didDefaultIndividual.current) return;
    if (isLoading || !draftChecked) return;       // wait until the draft check settles
    if (state.userType) return;                   // restored draft / explicit choice wins
    if (searchParams.get('type')) return;         // an explicit ?type= is handled above
    if (draftId) return;                          // a draft exists → leave it to draft handling
    didDefaultIndividual.current = true;
    setUserType('individual');
  }, [isLoading, draftChecked, state.userType, draftId, searchParams, setUserType]);

  // Fetch tiers when user type is broker or company
  useEffect(() => {
    if (state.userType === 'broker' || state.userType === 'company' || state.userType === 'developer') {
      registrationService.getTiers(state.userType).then(setTiers).catch(console.error);
    }
  }, [state.userType]);

  // Get selected tier for payment
  const selectedTier = tiers.find((tier) => tier.id === state.selectedTierId);
  const paymentAmount = state.billingCycle === 'yearly'
    ? selectedTier?.priceYearly || 0
    : selectedTier?.priceMonthly || 0;

  // Current step id
  const currentStepId = steps[currentStep]?.id;

  // Handle advancing from account step — calls signUp to initiate registration and send OTP
  const handleAdvanceFromAccount = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const { firstName, lastName, email, password, phone, gender, nationality, birthYear } = state.accountInfo;

      // For broker/company, pass business info to signUp for server-side storage
      const extraData: Record<string, unknown> = {
        firstName,
        lastName,
        phoneNumber: phone,
        accountType: state.userType as UserType,
        // Optional self-reported demographics (Statistics platform)
        ...(gender ? { gender } : {}),
        ...(nationality ? { nationality } : {}),
        ...(birthYear ? { birthYear } : {}),
      };

      if (state.userType === 'broker' || state.userType === 'company') {
        if (state.businessInfo.companyName) extraData.companyName = state.businessInfo.companyName;
        if (state.businessInfo.falLicense) extraData.falLicenseNumber = state.businessInfo.falLicense;
      }
      if (state.userType === 'developer') {
        if (state.businessInfo.companyName) extraData.developerName = state.businessInfo.companyName;
        if (state.businessInfo.falLicense) extraData.falLicenseNumber = state.businessInfo.falLicense;
      }

      const response = await signUp(email, password, extraData);

      if (response.requiresVerification) {
        // OTP sent — advance to email-verify step (EmailVerifyStep manages its own countdown)
        goNext();
      } else {
        // No verification required — account created, advance wizard
        setEmailVerified(true);
        goNext();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [state.accountInfo, state.userType, state.businessInfo, signUp, goNext, setEmailVerified]);

  // Handle final submission at review step
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      // For broker/company, create company/agent records with the business info
      // collected during the wizard (the initial signUp didn't have this data yet)
      if (pendingSession?.token && (state.userType === 'company' || state.userType === 'broker')) {
        await apiRequest('/auth/complete-profile', {
          method: 'POST',
          token: pendingSession.token,
          body: {
            companyName: state.businessInfo.companyName,
            falLicenseNumber: state.businessInfo.falLicense,
            phone: state.businessInfo.phone,
          },
        });
      }
      // Developers: create the Developer entity + owner membership at the end.
      if (pendingSession?.token && state.userType === 'developer') {
        await apiRequest('/auth/complete-profile', {
          method: 'POST',
          token: pendingSession.token,
          body: {
            developerName: state.businessInfo.companyName,
            falLicenseNumber: state.businessInfo.falLicense,
            phone: state.businessInfo.phone,
          },
        });
      }

      // Activate the session now that the full registration flow is complete.
      // The await on clearDraft() gives React a chance to flush the setState
      // from activateSession before we navigate — without that yield, the
      // success page could mount before useAuth's internal session state
      // reflected the activation, and AvatarUpload's "must be signed in"
      // guard would fire.
      if (pendingSession) {
        activateSession(pendingSession);
      }
      await clearDraft();
      // Extra microtask before navigation so React commits the activation.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      // Replace so Back from the welcome screen leaves sign-up entirely.
      navigate('/register/success?type=' + state.userType, { replace: true });
    } catch (error) {
      console.error('Submit failed:', error);
      const message = error instanceof Error ? error.message : 'Submission failed. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [state.userType, state.businessInfo, clearDraft, navigate, pendingSession, activateSession]);

  // Handle "Next" button click — routes to special handler for account step
  const handleNext = useCallback(() => {
    if (currentStepId === 'account') {
      handleAdvanceFromAccount();
    } else {
      goNext();
    }
  }, [currentStepId, handleAdvanceFromAccount, goNext]);

  // Map step id to i18n label key
  const getStepLabel = (stepId: string) => {
    const stepKeyMap: Record<string, string> = {
      'account': 'account',
      'email-verify': 'emailVerify',
      'property-info': 'propertyInfo',
      'contact': 'contact',
      'business-info': 'businessInfo',
      'subscription': 'subscription',
      'payment': 'payment',
      'review': 'review',
    };
    return t(`steps.${stepKeyMap[stepId] || stepId}`);
  };

  // Handle account info changes
  const handleAccountInfoChange = useCallback((data: typeof state.accountInfo) => {
    updateAccountInfo(data);
  }, [updateAccountInfo]);

  // Handle business info changes
  const handleBusinessInfoChange = useCallback((data: typeof state.businessInfo) => {
    updateBusinessInfo(data);
  }, [updateBusinessInfo]);

  // Render step content
  const renderStepContent = () => {
    // If no user type selected, show type selector
    if (!state.userType) {
      return (
        <UserTypeSelector
          selectedType={state.userType}
          onSelect={setUserType}
        />
      );
    }

    const stepId = steps[currentStep]?.id;

    // email-verify step is the same for all user types
    if (stepId === 'email-verify') {
      return (
        <EmailVerifyStep
          email={state.accountInfo.email}
          alreadyVerified={state.emailVerified}
          onVerified={(session) => {
            setPendingSession(session);
            setEmailVerified(true);
            // Use goToStep directly — goNext() has a stale closure where
            // emailVerified is still false, causing it to block navigation
            goToStep(currentStep + 1);
          }}
          onChangeEmail={() => {
            // User wants a different email — clear all downstream state so
            // the new account starts fresh (no business info or plan
            // selection bleeding over from the previous attempt).
            setEmailVerified(false);
            goPrevious();
          }}
          onContinue={() => goToStep(currentStep + 1)}
        />
      );
    }

    // Individual flow
    if (state.userType === 'individual') {
      switch (stepId) {
        case 'account':
          return (
            <>
              <AccountInfoForm
                initialData={state.accountInfo}
                onChange={handleAccountInfoChange}
                onValidationChange={setStepValid}
              />
              {SIGNUP_INDIVIDUAL_FIRST && (
                <div className="max-w-lg mx-auto mt-5 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
                  <button
                    type="button"
                    onClick={() => setUserType(null as unknown as UserType)}
                    className="text-sm font-medium text-[#004D40] hover:text-[#003930] hover:underline"
                  >
                    {isRTL
                      ? 'هل أنت وسيط أو شركة؟ سجّل كحساب أعمال ←'
                      : 'Registering as a broker or company? Switch to a business account →'}
                  </button>
                </div>
              )}
            </>
          );
        case 'review':
          return (
            <div className="max-w-2xl mx-auto space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
                  {t('review.title')}
                </h2>
                <p className="text-gray-500">{t('review.subtitle')}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl border p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    {t('account.title')}
                  </h3>
                  <button
                    type="button"
                    onClick={() => goToStep(0)}
                    className="text-sm text-[#004D40] hover:text-[#003930]"
                  >
                    {t('review.edit')}
                  </button>
                </div>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">{t('account.firstName')}</dt>
                    <dd className="font-medium">{state.accountInfo.firstName}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">{t('account.lastName')}</dt>
                    <dd className="font-medium">{state.accountInfo.lastName}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">{t('account.email')}</dt>
                    <dd className="font-medium" dir="ltr">{state.accountInfo.email}</dd>
                  </div>
                  {state.accountInfo.phone && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500">{t('account.phone')}</dt>
                      <dd className="font-medium" dir="ltr">{state.accountInfo.phone}</dd>
                    </div>
                  )}
                </dl>
              </div>
              <p className="text-sm text-gray-500 text-center">
                {t('review.termsText')}{' '}
                <Link to="/terms" className="text-[#004D40] hover:underline">
                  {t('review.termsLink')}
                </Link>{' '}
                {t('review.andText')}{' '}
                <Link to="/privacy" className="text-[#004D40] hover:underline">
                  {t('review.privacyLink')}
                </Link>
              </p>
            </div>
          );
      }
    }

    // Broker/Company flow
    switch (stepId) {
      case 'account':
        return (
          <AccountInfoForm
            initialData={state.accountInfo}
            onChange={handleAccountInfoChange}
            onValidationChange={setStepValid}
          />
        );
      case 'business-info':
        return (
          <BusinessInfoForm
            userType={state.userType}
            initialData={state.businessInfo}
            onChange={handleBusinessInfoChange}
            onValidationChange={setStepValid}
          />
        );
      case 'subscription':
        return (
          <div className="space-y-8">
            <TierSelector
              userType={state.userType as 'broker' | 'company'}
              selectedTierId={state.selectedTierId}
              billingCycle={state.billingCycle}
              skippedMembership={state.skippedMembership}
              onSelectTier={setSelectedTier}
              onChangeBillingCycle={setBillingCycle}
            />
            <div className="max-w-md mx-auto">
              <PromoCodeInput
                userType={state.userType as 'broker' | 'company'}
                currentCode={state.promoCode}
                validation={state.promoValidation}
                purchaseAmount={paymentAmount}
                onCodeChange={setPromoCode}
                onValidationResult={() => {
                  // Promo validation is handled by the PromoCodeInput component
                }}
              />
            </div>
            {/* Skip for now — proper outline button so it's discoverable.
                The wizard hook clears selectedTierId + jumps to review. */}
            <div className="text-center max-w-md mx-auto">
              <button
                type="button"
                onClick={() => skipMembership()}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <i className="ri-skip-forward-line" aria-hidden="true" />
                {t('subscription.skipForNow')}
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {t('subscription.skipNote')}
              </p>
            </div>
          </div>
        );
      case 'payment':
        return (
          <div className="max-w-md mx-auto">
            <PaymentForm
              amount={paymentAmount}
              planId={selectedTier?.id || ''}
              tierName={selectedTier?.name || ''}
              billingCycle={state.billingCycle}
              returnUrl={`/register/success?type=${state.userType || 'company'}`}
              onPaymentInitiated={(id) => {
                console.log('Payment initiated:', id);
              }}
              onPaymentError={(error) => {
                console.error('Payment error:', error);
              }}
            />
          </div>
        );
      case 'review':
        return (
          <ReviewStep
            state={state}
            tiers={tiers}
            steps={steps}
            onEdit={goToStep}
          />
        );
    }

    return null;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#004D40] mx-auto" />
          <p className="mt-4 text-gray-500">{t('buttons.loading')}</p>
        </div>
      </div>
    );
  }

  // Draft recovery prompt
  if (showDraftPrompt) {
    return (
      <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
        <Navigation />

        <div className="pt-20 pb-12 px-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 md:p-8" dir={isRTL ? 'rtl' : 'ltr'}>
              <div className="text-center mb-6">
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <i className="ri-draft-line text-3xl text-amber-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
                  {t('draft.found')}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {state.userType === 'broker'
                    ? t('userType.broker.title')
                    : state.userType === 'company'
                    ? t('userType.company.title')
                    : t('userType.individual.title')}
                  {' - '}
                  {/* The step's translated name (the wizard's own labels are English). */}
                  {getStepLabel(steps[state.currentStep]?.id ?? 'account')}
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleContinueDraft}
                  className="w-full"
                >
                  <i className={`ri-arrow-right-line ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`}></i>
                  {t('draft.continue')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleStartFresh}
                  className="w-full"
                >
                  <i className={`ri-refresh-line ${isRTL ? 'ml-2' : 'mr-2'}`}></i>
                  {t('draft.startOver')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  const showStepper = state.userType && steps.length > 0;
  const isLastStep = currentStep === steps.length - 1;

  // For email-verify step: hide the default navigation buttons (EmailVerifyStep manages its own)
  const isEmailVerifyStep = currentStepId === 'email-verify';

  return (
    <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
      <Navigation />

      <div className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Stepper */}
          {showStepper && (
            <div className="mb-8">
              <Stepper
                steps={steps.map((s) => ({
                  id: s.id,
                  label: getStepLabel(s.id),
                }))}
                currentStep={currentStep}
                allowClickPrevious
                onStepClick={goToStep}
              />
            </div>
          )}

          {/* Content Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 md:p-8">
            {submitError && (
              <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-xl">
                <i className="ri-error-warning-line mr-2"></i>
                {submitError}
              </div>
            )}
            {renderStepContent()}

            {/* Navigation Buttons — hidden on email-verify step (it handles its own buttons) */}
            {!isEmailVerifyStep && (
              <div className={`
                mt-8 pt-6 border-t flex items-center
                ${state.userType ? 'justify-between' : 'justify-center'}
              `}>
                {/* Back Button */}
                {state.userType && (canGoPrevious || currentStep === 0) && (
                  <Button
                    variant="outline"
                    onClick={currentStep === 0 ? () => setUserType(null as unknown as UserType) : goPrevious}
                    disabled={isSubmitting}
                  >
                    <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
                    {t('buttons.back')}
                  </Button>
                )}

                {/* Spacer when no back button */}
                {state.userType && !canGoPrevious && currentStep !== 0 && <div />}

                {/* Continue/Submit Button */}
                {state.userType && (
                  <Button
                    onClick={isLastStep ? handleSubmit : handleNext}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    {isLastStep ? (
                      t('review.confirm')
                    ) : (
                      <>
                        {t('buttons.continue')}
                        <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}

            {/* Saving indicator */}
            {isSaving && (
              <p className="text-center text-sm text-gray-500 mt-4 flex items-center justify-center gap-2">
                <Save className="w-4 h-4 animate-pulse" aria-hidden="true" />
                {isRTL ? 'جاري الحفظ…' : 'Saving…'}
              </p>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
